import { chartCls } from "../chartClasses.js";
import { ensureAn26DesignStyles } from "./injectStyles.js";
import type { StatisticsPayload } from "../normalize.js";
import type { ChartLayout } from "../types.js";
import { formatRichTextHtml } from "./richText.js";

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export interface StatisticsRenderOptions {
  title?: string | null;
  disclaimer?: string | null;
  layout?: ChartLayout;
}

export function mountStatistics(
  container: HTMLElement,
  data: StatisticsPayload,
  opts?: StatisticsRenderOptions
): () => void {
  ensureAn26DesignStyles();
  const embedded = opts?.layout === "embedded";
  const subParts: string[] = [`As of ${data.asOf}`];
  if (data.frequency) subParts.push(`(${data.frequency})`);
  const subtitle = subParts.join(" ");
  const disclaimer =
    opts?.disclaimer === null ? "" : (opts?.disclaimer ?? data.disclaimer ?? "");

  const rows = data.rows
    .map(
      (r) => `<tr>
      <td class="an26-stats__label">${esc(r.label)}</td>
      <td class="an26-stats__num">${esc(r.portfolio)}</td>
      <td class="an26-stats__num">${esc(r.benchmark)}</td>
    </tr>`
    )
    .join("");

  const paneTitleCls = chartCls("tabbed__pane-title");
  const head = embedded
    ? ""
    : `<header class="an26-root__head">
        <h2 class="an26-root__title">Portfolio</h2>
        <h3 class="${paneTitleCls}">Statistics</h3>
        <p class="an26-root__subtitle an26-root__subtitle--stats">${esc(subtitle)}</p>
      </header>`;
  const rootClass = embedded ? "an26-root an26-root--stats an26-root--embedded" : "an26-root an26-root--stats";

  container.innerHTML = `
    <div class="${rootClass}">
      ${head}
      <div class="an26-split an26-split--stats">
        <div class="an26-stats__tablewrap">
          <table class="an26-table an26-stats__table" role="grid">
            <thead>
              <tr>
                <th class="an26-stats__th-corner" aria-hidden="true"></th>
                <th class="an26-stats__th-num">Portfolio</th>
                <th class="an26-stats__th-num">Benchmark</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
        <aside class="an26-stats__aside">
          ${disclaimer ? `<p class="an26-stats__disc">${formatRichTextHtml(disclaimer)}</p>` : ""}
        </aside>
      </div>
    </div>`;

  return () => container.replaceChildren();
}
